import { Icon } from '@iconify/react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import type { BadmintonGameListItem } from '@/services/badmintonService';
import { countSetWins } from './badmintonScoringUtils';
import {
  formatCourtLabel,
  formatGameStatusLabel,
  formatPlayerFormat,
  playersLabel,
} from './badmintonUiUtils';

interface BadmintonMatchCardProps {
  game: BadmintonGameListItem;
  gameNumber: number;
  canEdit: boolean;
  onEditScore?: () => void;
  onEditPlayer?: () => void;
  onDelete?: () => void;
}

function setScoreLine(side: 'a' | 'b', game: BadmintonGameListItem): string {
  return (game.setScores ?? [])
    .filter((s) => s.isComplete || s.scoreSideA > 0 || s.scoreSideB > 0)
    .map((s) => (side === 'a' ? s.scoreSideA : s.scoreSideB))
    .join(' · ');
}

export default function BadmintonMatchCard({
  game,
  gameNumber,
  canEdit,
  onEditScore,
  onEditPlayer,
  onDelete,
}: BadmintonMatchCardProps) {
  const scoringType = game.scoringType || 'rally_21';
  const { winsA, winsB } = countSetWins(game.setScores ?? [], scoringType);
  const scheduled = game.status === 'scheduled';
  const hasScore = (game.setScores ?? []).some((s) => s.scoreSideA > 0 || s.scoreSideB > 0);
  const winnerA = !scheduled && winsA > winsB;
  const winnerB = !scheduled && winsB > winsA;
  const labelA = playersLabel(game.sideAPlayers) || 'Sisi A';
  const labelB = playersLabel(game.sideBPlayers) || 'Sisi B';
  const courtLabel = formatCourtLabel(game.courtNumber, game.sessionNumCourts);

  return (
    <Card className="rounded-2xl border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#E0E6ED]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge className="border-0 bg-[#FFF1EC] px-2.5 py-1 text-[10px] font-bold text-[#FC480D] hover:bg-[#FFF1EC]">
            {formatPlayerFormat(game.playerFormat)}
          </Badge>
          <span className="text-xs font-semibold text-gray-400">
            Game {gameNumber}
            {courtLabel ? ` · ${courtLabel}` : ''}
          </span>
        </div>
        {scheduled ? (
          <Badge variant="outline" className="border-0 text-xs font-semibold text-gray-400">
            {formatGameStatusLabel('scheduled')}
          </Badge>
        ) : (
          <span className="text-xs font-semibold text-gray-500">
            Set {winsA} - {winsB}
          </span>
        )}
      </div>

      <div className="mt-3 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <p className={`min-w-0 flex-1 text-sm ${winnerA ? 'font-bold text-[#0E1726]' : 'text-[#0E1726]'}`}>
            {winnerA && (
              <Icon icon="mdi:check-circle" className="mr-1 inline h-4 w-4 text-emerald-500" />
            )}
            {labelA}
          </p>
          {!scheduled && (
            <span className={`text-sm ${winnerA ? 'font-bold text-[#0E1726]' : 'text-[#0E1726]'}`}>
              {setScoreLine('a', game)}
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-3">
          <p className={`min-w-0 flex-1 text-sm ${winnerB ? 'font-bold text-[#0E1726]' : 'text-[#0E1726]'}`}>
            {winnerB && (
              <Icon icon="mdi:check-circle" className="mr-1 inline h-4 w-4 text-emerald-500" />
            )}
            {labelB}
          </p>
          {!scheduled && (
            <span className={`text-sm ${winnerB ? 'font-bold text-[#0E1726]' : 'text-[#0E1726]'}`}>
              {setScoreLine('b', game)}
            </span>
          )}
        </div>
      </div>

      {canEdit && (
        <div className="mt-3 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onEditScore}
            className="h-10 flex-1 rounded-full border-gray-200 text-sm font-semibold text-[#0E1726]"
          >
            <Icon icon="mdi:pencil-outline" className="h-4 w-4" />
            Edit Skor
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={hasScore}
            onClick={onEditPlayer}
            className="h-10 flex-1 rounded-full border-gray-200 text-sm font-semibold text-[#0E1726]"
          >
            <Icon icon="mdi:account-switch-outline" className="h-4 w-4" />
            Edit Player
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Hapus Game ${gameNumber}`}
            onClick={onDelete}
            className="h-10 w-10 rounded-full border-red-100 text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <Icon icon="mdi:trash-can-outline" className="h-4 w-4" />
          </Button>
        </div>
      )}
    </Card>
  );
}
